import { type ColProps } from "antd";

import { asTuple } from "utilities/array/array";
import yup from "utilities/form/yup";

import {
    type IInputEmailProps,
    type InputEmailFormValue,
} from "commonComponents/entity/email/InputEmail/InputEmail.types";

const narrowToColProps = (layout: string | number | ColProps) =>
    typeof layout === "number" || typeof layout === "string" ? { span: layout } : layout;

export const convertColLayoutToProps = (
    columnLayout: Required<IInputEmailProps>["columnLayout"],
) => {
    let emailColProps: ColProps;
    let labelColProps: ColProps;
    if (columnLayout === "auto") {
        const autoColProps = { flex: "1 1 0" };
        emailColProps = autoColProps;
        labelColProps = autoColProps;
    } else {
        emailColProps = narrowToColProps(columnLayout[0]);
        labelColProps = narrowToColProps(columnLayout[1]);
    }

    return asTuple(emailColProps, labelColProps);
};

/**
 * Builds a mailto: href that includes the friendlyName as the display name
 * when present. The result is RFC 6068's name-and-address form:
 *   mailto:Friendly%20Name%20%3Cemail%40example.com%3E
 * with the display portion URL-encoded so reserved characters can't break out
 * of the URI. Angle brackets and double quotes are stripped first so the
 * display-name portion stays well-formed (the friendlyName validator already
 * rejects ";" and "," at form-input time).
 */
export function buildMailtoHref(emailAddress: string, friendlyName?: string | null): string {
    const trimmedName = friendlyName?.trim();
    if (!trimmedName) {
        return `mailto:${emailAddress}`;
    }
    const sanitizedName = trimmedName.replace(/[<>"]/g, "");
    return `mailto:${encodeURIComponent(`${sanitizedName} <${emailAddress}>`)}`;
}

export const InputEmailValidators = {
    key: yup.string().label("Key"),
    emailAddress: yup.string().email().max(110).label("Email"),
    friendlyName: yup
        .string()
        .max(50)
        .matches(/^[^;,]*$/, {
            message: "Invalid Label, do not use ; ,",
            excludeEmptyString: true,
        })
        .label("Label"),
    fullEmail: yup.string().nullable().label("Full Email"),
};

export const InputEmailValidator = yup.object().shape<InputEmailFormValue>(InputEmailValidators);
